import axios from "axios";
import { useEffect, useState } from "react";
import { PrivateUser } from "../../CRUD/CampSites/types";
import deletePrivateUser from "./deletePrivateUser";
import { format } from "date-fns";

interface PrivateUserDetailProps {
  privateUser: PrivateUser;
  onEdit: (privateUser: PrivateUser, isEditing: boolean) => void;
  onClose: () => void;
}

const PrivateUserDetail: React.FC<PrivateUserDetailProps> = ({
  privateUser,
  onClose,
  onEdit,
}) => {
  const [bookings, setBookings] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    const fetchPrivateUser = async () => {
      try {
        const response = await axios.get<{ data: { bookings: any[] } }>(
          `${process.env.REACT_APP_API_URL}/PrivateUser/getPrivateUser/${privateUser.id}`
        );

        console.log("Private user details:", response.data);
        setBookings(response.data.data.bookings);
        setLoading(false);
      } catch (error) {
        console.error("Error fetching private user details", error);
        setLoading(false);
      }
    };

    fetchPrivateUser();
  }, [privateUser.id]);

  const handleBack = () => {
    onClose();
  };

  const handleEdit = () => {
    onEdit(privateUser, true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  const handleDeletePrivateUser = async (Id: number) => {
    if (window.confirm("Are you sure you want to delete this private user?")) {
      try {
        await deletePrivateUser(Id);
        alert("Private user deleted successfully.");
        onClose(); // Close the detail view after deletion
      } catch (error) {
        console.error("Error deleting private user:", error);
        alert("Failed to delete private user.");
      }
    }
  };

  return (
    <div className="container mt-4">
      <div className="card-private-details">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5>Private User Details</h5>
        </div>
        <div className="card-body">
          <div className="row mb-3">
            <div className="col-sm-4 font-weight-bold">Name:</div>
            <div className="col-sm-8">{privateUser.name}</div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-4 font-weight-bold">Email:</div>
            <div className="col-sm-8">{privateUser.email}</div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-4 font-weight-bold">Phone:</div>
            <div className="col-sm-8">{privateUser.phoneNumber}</div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-4 font-weight-bold">Address:</div>
            <div className="col-sm-8">{privateUser.address}</div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-4 font-weight-bold">Address 2:</div>
            <div className="col-sm-8">{privateUser.address2}</div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-4 font-weight-bold">City:</div>
            <div className="col-sm-8">{privateUser.city}</div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-4 font-weight-bold">State:</div>
            <div className="col-sm-8">{privateUser.state}</div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-4 font-weight-bold">Country:</div>
            <div className="col-sm-8">{privateUser.country}</div>
          </div>
          <div className="row mb-3">
            <div className="col-sm-4 font-weight-bold">Bookings:</div>
            <div className="col-sm-8">
              {loading ? (
                <p>Loading bookings...</p>
              ) : bookings.length > 0 ? (
                <ul className="list-group">
                  {bookings.map((booking) => (
                    <li key={booking.id} className="list-group-item">
                      <div className="booking-field">
                        <strong>Order Number:</strong> {booking.orderNumber}
                      </div>
                      <div className="booking-field">
                        <strong>Arrival Date:</strong> {format(new Date(Date.parse(booking.arrivalDate)), "yyyy-MM-dd")}
                      </div>
                      <div className="booking-field">
                        <strong>Departure Date:</strong> {format(new Date(Date.parse(booking.departureDate)), "yyyy-MM-dd")}
                      </div>
                      <div className="booking-field">
                        <strong>Total Price:</strong> {formatCurrency(booking.totalPrice)}
                      </div>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>No bookings available.</p>
              )}
            </div>
          </div>
        </div>
        <div className="card-footer d-flex justify-content-end">
          <button
            className="btn btn-sm btn-outline-danger me-2"
            onClick={() => handleDeletePrivateUser(privateUser.id)}
            disabled={bookings.length > 0}
          >
            <i className="fas fa-trash-alt"></i> Delete
          </button>
          <button
            className="btn btn-sm btn-outline-primary me-2"
            onClick={handleEdit}
          >
            <i className="fas fa-edit"></i> Edit
          </button>
          <button
            className="btn btn-sm btn-outline-secondary me-2"
            onClick={handleBack}
          >
            <i className="fas fa-times"></i> Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default PrivateUserDetail;